import { FileChip } from "@/components/file/FileChip";
import { AllowedMimeType } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperclip } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { MAX_FILES_COUNT, useChatFileManager } from "./ChatFileManagerContext";

type ChatFileManagerProps = {
  goal: "select" | "upload";
};

/**
 * Needs to be used within a ChatFileManagerProvider.
 */
export const ChatFileManager: React.FC<ChatFileManagerProps> = ({ goal }) => {
  const {
    selectedLocalFiles,
    selectedExternalFiles,
    selectedLibraryFiles,
    chatFiles,
    filesCount,
    allowedMimeTypes,
    fileLimitExceeded,
    removeLocalFile,
    removeExternalFile,
    removeLibraryFile,
  } = useChatFileManager();

  // ============================================================
  // Renderer
  // ============================================================

  return (
    <>
      <div>
        {filesCount > 0 && (
          <>
            <div className="border-border flex flex-row items-start gap-2 border-t-1 pt-2">
              <div className="text-muted-foreground flex flex-row items-center gap-2 pt-2 text-xs">
                <FontAwesomeIcon icon={faPaperclip} />
                {filesCount}/{MAX_FILES_COUNT}
              </div>
              <div className="flex flex-row flex-wrap gap-2 px-1">
                {selectedLocalFiles.map((file) => {
                  return (
                    <FileChip
                      key={file.name}
                      file={file}
                      loading={goal === "upload"}
                      onRemove={() => {
                        removeLocalFile(file);
                      }}
                    />
                  );
                })}
                {selectedExternalFiles.map((file) => {
                  return (
                    <FileChip
                      key={file.externalId}
                      file={file}
                      loading={goal === "upload"}
                      onRemove={() => {
                        removeExternalFile(file.externalId);
                      }}
                    />
                  );
                })}
                {[...chatFiles, ...selectedLibraryFiles].map((file) => {
                  const isFileTypeAllowed = allowedMimeTypes.includes(
                    file.mimeType as AllowedMimeType,
                  );
                  return (
                    <FileChip
                      key={file.uuid}
                      file={file}
                      onRemove={() => {
                        removeLibraryFile(file.uuid);
                      }}
                      unavailable={
                        !isFileTypeAllowed
                          ? `This file type is not supported by the selected model and will be ignored`
                          : undefined
                      }
                    />
                  );
                })}
              </div>
            </div>
          </>
        )}

        {fileLimitExceeded && (
          <p className="text-destructive px-1 text-xs">
            You can attach a maximum of 5 files to a chat. Remove a file before
            adding another.
          </p>
        )}
      </div>
    </>
  );
};
